import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useTheme } from 'react-native-paper';
import { TextInput } from 'react-native-paper';
import QText from './QText';
import FontAwesome from '@expo/vector-icons/FontAwesome';

const CategorySelector = ({
  label,
  value,
  onChangeText,
  onBlur,
  error,
  errorText,
  required = false,
}) => {
  const theme = useTheme();
  const [isCustomInput, setIsCustomInput] = useState(false);

  // Predefined categories based on the image
  const predefinedCategories = [
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: '🏥',
      description: 'Hospitals, clinics, pharmacies',
    },
    {
      id: 'banking',
      name: 'Banking',
      icon: '🏦',
      description: 'Banks, ATMs, financial services',
    },
    {
      id: 'education',
      name: 'Education',
      icon: '🎓',
      description: 'Schools, universities, training centers',
    },
    {
      id: 'retail',
      name: 'Retail',
      icon: '🛍️',
      description: 'Stores, malls, shopping centers',
    },
    {
      id: 'restaurant',
      name: 'Restaurant & Food',
      icon: '🍽️',
      description: 'Restaurants, cafes, food services',
    },
    {
      id: 'automotive',
      name: 'Automotive',
      icon: '🚗',
      description: 'Car dealerships, service centers',
    },
    {
      id: 'technology',
      name: 'Technology',
      icon: '💻',
      description: 'IT services, software companies',
    },
    {
      id: 'real_estate',
      name: 'Real Estate',
      icon: '🏠',
      description: 'Property agencies, developers',
    },
  ];

  const handleCategorySelect = (category) => {
    onChangeText(category.name);
    setIsCustomInput(false);
  };

  const handleCustomInput = () => {
    setIsCustomInput(true);
    onChangeText('');
  };

  const handleInputChange = (text) => {
    onChangeText(text);
  };

  const isCategorySelected = (categoryName) => {
    return value === categoryName;
  };

  const getSelectedCategory = () => {
    return predefinedCategories.find((cat) => cat.name === value);
  };

  return (
    <View style={styles.container}>
      {/* Label */}
      <View style={styles.labelContainer}>
        <QText style={[styles.label, { color: theme.colors.onSurface }]}>
          {label}
          {required ? ' *' : ''}
        </QText>
      </View>

      {/* Selected Category Display */}
      {value && (
        <View
          style={[
            styles.selectedCategory,
            { backgroundColor: theme.colors.primaryContainer },
          ]}
        >
          <View style={styles.selectedCategoryContent}>
            {getSelectedCategory() ? (
              // Predefined category
              <>
                <QText style={styles.categoryIconText}>
                  {getSelectedCategory().icon}
                </QText>
                <View style={styles.selectedCategoryInfo}>
                  <QText
                    style={[
                      styles.selectedCategoryName,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    {getSelectedCategory().name}
                  </QText>
                  <QText
                    style={[
                      styles.selectedCategoryDescription,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    {getSelectedCategory().description}
                  </QText>
                </View>
              </>
            ) : (
              // Custom category
              <>
                <View style={styles.customCategoryIcon}>
                  <FontAwesome
                    name="tag"
                    size={20}
                    color={theme.colors.primary}
                  />
                </View>
                <View style={styles.selectedCategoryInfo}>
                  <QText
                    style={[
                      styles.selectedCategoryName,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    {value}
                  </QText>
                  <QText
                    style={[
                      styles.selectedCategoryDescription,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Custom category
                  </QText>
                </View>
              </>
            )}
            <TouchableOpacity
              style={styles.changeButton}
              onPress={() => {
                if (getSelectedCategory()) {
                  // If it's a predefined category, go back to options
                  setIsCustomInput(false);
                } else {
                  // If it's a custom category, go to custom input mode
                  setIsCustomInput(true);
                }
              }}
            >
              <FontAwesome name="edit" size={14} color={theme.colors.primary} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Custom Input Field */}
      {isCustomInput && (
        <View style={styles.customInputContainer}>
          <View style={styles.customInputRow}>
            <TextInput
              label="Enter custom category"
              value={value}
              onChangeText={handleInputChange}
              onBlur={onBlur}
              error={error}
              style={[styles.customInput, styles.customInputField]}
              autoFocus={true}
              maxLength={50}
            />
            <TouchableOpacity
              style={[
                styles.addCustomButton,
                { backgroundColor: theme.colors.primary },
                !value?.trim() && { opacity: 0.5 },
              ]}
              onPress={() => {
                if (value?.trim()) {
                  // Add the custom category and exit custom input mode
                  onChangeText(value.trim());
                  setIsCustomInput(false);
                }
              }}
              disabled={!value?.trim()}
              activeOpacity={0.8}
            >
              <FontAwesome name="plus" size={16} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={styles.backToOptionsButton}
            onPress={() => setIsCustomInput(false)}
          >
            <FontAwesome
              name="arrow-left"
              size={14}
              color={theme.colors.primary}
            />
            <QText
              style={[styles.backButtonText, { color: theme.colors.primary }]}
            >
              Back to options
            </QText>
          </TouchableOpacity>
        </View>
      )}

      {/* Category Options Grid */}
      {!isCustomInput && (
        <View style={styles.optionsContainer}>
          <View style={styles.optionsHeader}>
            <QText
              style={[styles.optionsTitle, { color: theme.colors.onSurface }]}
            >
              Choose a category
            </QText>
            <TouchableOpacity
              onPress={handleCustomInput}
              style={styles.customButton}
            >
              <FontAwesome name="plus" size={14} color={theme.colors.primary} />
              <QText
                style={[
                  styles.customButtonText,
                  { color: theme.colors.primary },
                ]}
              >
                Custom
              </QText>
            </TouchableOpacity>
          </View>

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.categoriesScroll}
          >
            {predefinedCategories.map((category) => (
              <TouchableOpacity
                key={category.id}
                style={[
                  styles.categoryButton,
                  isCategorySelected(category.name) && {
                    backgroundColor: theme.colors.primary || '#4CAF50',
                    borderColor: theme.colors.primary || '#4CAF50',
                    shadowColor: theme.colors.primary || '#4CAF50',
                    shadowOpacity: 0.2,
                  },
                ]}
                onPress={() => handleCategorySelect(category)}
                activeOpacity={0.7}
              >
                <View style={styles.categoryButtonContent}>
                  <QText style={styles.categoryButtonIcon}>
                    {category.icon}
                  </QText>
                  <QText
                    style={[
                      styles.categoryButtonName,
                      isCategorySelected(category.name)
                        ? { color: '#FFFFFF' }
                        : { color: '#FFFFFF' },
                    ]}
                  >
                    {category.name}
                  </QText>
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Error Message */}
      {error && errorText && (
        <QText style={[styles.errorText, { color: theme.colors.error }]}>
          {errorText}
        </QText>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  labelContainer: {
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
  },
  selectedCategory: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  selectedCategoryContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectedCategoryInfo: {
    flex: 1,
    marginLeft: 12,
  },
  selectedCategoryName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  selectedCategoryDescription: {
    fontSize: 12,
  },
  changeButton: {
    padding: 8,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  categoryIconText: {
    fontSize: 24,
  },
  customCategoryIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  customInputContainer: {
    marginBottom: 12,
  },
  customInputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
  },
  customInput: {
    backgroundColor: 'transparent',
  },
  customInputField: {
    flex: 1,
  },
  addCustomButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  backToOptionsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
    marginTop: 8,
  },
  backButtonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  optionsContainer: {
    marginTop: 8,
  },
  optionsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  optionsTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  customButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  customButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  categoriesScroll: {
    paddingRight: 16,
  },
  categoryButton: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
    minWidth: 100,
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(255, 255, 255, 0.15)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  categoryButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  categoryButtonIcon: {
    fontSize: 20,
  },
  categoryButtonName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  errorText: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 4,
  },
});

export default CategorySelector;
